import { computed, ref, shallowRef } from 'vue';
import { EventEmitter, Listener } from './event-emitter';
import { clamp, round } from 'lodash';
import Clock from 'phaser3-rex-plugins/plugins/clock.js';
import BaseScene from 'src/scenes/base';

const useCountdownClock = <T extends Phaser.Scene> (phaserScene?: T) => {

    const events = new EventEmitter();
    const scene = shallowRef<Phaser.Scene>();
    const clock = shallowRef<Clock>();
    const countdownMs = ref<number>(0);
    const duration = ref<number>(0);
    const remaining = ref(0);
    const progress = ref(0);
    const disabled = ref(false);
    const state = ref<'running' | 'stopped' | 'paused'>('stopped');

    const recalc = () => {
        if (clock.value){
            progress.value = clamp(round(1 - (Math.floor(clock.value!.now) / countdownMs.value), 4), 0, 1);
            remaining.value = clamp(countdownMs.value - clock.value.now, 0, countdownMs.value);
        }
    }

    const start = (countdownMillis: number) => {
        if (disabled.value) return;
        if (clock.value){
            clock.value.stop();
            countdownMs.value = countdownMillis;
            duration.value = countdownMillis; //Just for external reference
            state.value = 'running';
            clock.value.start();
        }
    }

    const stop = () => {
        if (disabled.value) return;
        if (clock.value){
            clock.value.stop();
            countdownMs.value = 0;
            duration.value = 0;
            state.value = 'stopped';
            recalc();
            events.emit('stop');
        }
    }

    const pause = () => {
        if (disabled.value) return;
        if (state.value === 'running' && clock.value){
            clock.value.pause();
            state.value = 'paused';
            events.emit('pause');
        }
    }

    const resume = () => {
        if (disabled.value) return;
        if (clock.value){
            state.value = 'running';
            clock.value.resume();
            events.emit('resume');
        }
    }

    const on = (event: string, listener: Listener) => events.on(event, listener);
    const removeListener = (event: string, listener: Listener) => events.removeListener(event, listener);
    const destroy = () => clock.value?.destroy();
    const populateScene = <T extends BaseScene | Phaser.Scene> (phaserScene: T) => {
        scene.value = phaserScene;
        clock.value = new Clock(phaserScene);
        clock.value.on('update', () => {
            recalc();
            events.emit('update');
            if (clock.value && clock.value.now >= countdownMs.value){
                clock.value.stop();
                events.emit('end');
            }
        })
    }

    const setDisabled = (val: boolean) => {
        disabled.value = val;
        if (val) stop();
    }
    
    const status = computed(() => state.value);

    if (phaserScene) {
        populateScene(phaserScene);
    } else {
        console.warn('useCountdownClock(): No scene provided to countdown clock. Use populateScene() to populate the scene if not available at the time of hook creation.')
    }
    return {
        populateScene,
        on,
        removeListener,
        stop,
        start,
        resume,
        pause,
        progress,
        duration,
        remaining,
        destroy,
        status,
        setDisabled
    }
}

export {
    useCountdownClock
}